import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FaSearch } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import serverURL from '../config';
import { useAuth } from '../context/AuthContext';
import AddUserModal from '../components/AddUserModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const ROLE_FILTERS = [
    { label: 'All', value: 'all' },
    { label: 'Admin', value: 'admin' },
    { label: 'Interviewer', value: 'interviewer' },
    { label: 'Candidate', value: 'candidate' },
];

const ROLE_BADGE = {
    admin: 'bg-red-100 text-red-700',
    interviewer: 'bg-blue-100 text-blue-700',
    candidate: 'bg-green-100 text-green-700',
};

const UserManagement = () => {
    const navigate = useNavigate();
    const { role } = useAuth();
    const isAdmin = role === 'admin';
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
        confirmText: '',
        confirmButtonClass: ''
    });

    const closeConfirmModal = () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1); // Reset to page 1 on new search
        }, 300);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${serverURL}/users`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    page: currentPage,
                    limit: rowsPerPage,
                    role: roleFilter,
                    search: debouncedSearch
                }
            });
            setUsers(response.data.data || []);
            setTotalPages(response.data.totalPages || 1);
            setTotalUsers(response.data.total || 0);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Failed to load users');
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, roleFilter, debouncedSearch, rowsPerPage]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleRoleChange = (role) => {
        setRoleFilter(role);
        setCurrentPage(1);
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) end = maxVisible - 1;
            if (currentPage >= totalPages - 2) start = totalPages - maxVisible + 2;

            if (start > 2) pages.push('...');
            for (let i = start; i <= end; i++) pages.push(i);
            if (end < totalPages - 1) pages.push('...');

            pages.push(totalPages);
        }
        return pages;
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUsers(users.map((u) => u.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (id) => {
        setSelectedUsers((prev) =>
            prev.includes(id) ? prev.filter((userId) => userId !== id) : [...prev, id]
        );
    };

    const handleToggleStatus = async (user) => {
        const previousUsers = [...users];
        const previousTotal = totalUsers;

        // Determine the new status before the API call
        const willBeActive = !user.is_active;

        // Optimistic update: update in place without removing
        setUsers(prev => prev.map(u =>
            u.id === user.id ? { ...u, is_active: willBeActive ? 1 : 0 } : u
        ));

        try {
            const token = localStorage.getItem('token');
            const response = await axios.patch(
                `${serverURL}/users/${user.id}/toggle-status`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(response.data.message);
            // Re-fetch to get accurate server-side pagination counts after a deactivation
            if (!willBeActive) {
                fetchUsers();
            }
        } catch (error) {
            console.error('Failed to toggle user status:', error);
            toast.error('Failed to change status');
            // Revert on failure
            setUsers(previousUsers);
            setTotalUsers(previousTotal);
        }
    };

    const confirmToggleStatus = (user) => {
        if (user.is_active) {
            setConfirmModal({
                isOpen: true,
                title: 'Deactivate User',
                message: `Are you sure you want to deactivate ${user.name}? `,
                confirmText: 'Deactivate',
                confirmButtonClass: 'bg-campus-error hover:bg-red-700',
                onConfirm: () => {
                    handleToggleStatus(user);
                    closeConfirmModal();
                }
            });
        } else {
            handleToggleStatus(user);
        }
    };

    const confirmBulkDeactivate = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Deactivate Users',
            message: `Are you sure you want to deactivate ${selectedUsers.length} users? They will no longer be able to log in.`,
            confirmText: 'Deactivate All',
            confirmButtonClass: 'bg-campus-error hover:bg-red-700',
            onConfirm: () => {
                handleBulkDeactivate();
                closeConfirmModal();
            }
        });
    };

    const handleBulkDeactivate = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.patch(
                `${serverURL}/users/bulk-deactivate`,
                { userIds: selectedUsers },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(response.data.message || 'Users deactivated successfully');
            setSelectedUsers([]);
            fetchUsers();
        } catch (error) {
            console.error('Failed to bulk deactivate:', error);
            toast.error('Failed to deactivate selected users');
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-campus-bg">
            <main className="flex-grow p-6">
                {/* Search + Filter toolbar - outer wrapper is relative so filter can anchor absolutely without affecting flex layout */}
                <div className="relative flex items-center gap-3 mb-5 flex-wrap">
                    {/* Left Side: Bulk Action & Add User */}
                    <div className="flex-shrink-0 min-w-[140px] flex items-center gap-2">

                        {isAdmin && selectedUsers.length > 0 && (
                            <button
                                onClick={confirmBulkDeactivate}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition shadow-sm whitespace-nowrap"
                            >
                                Deactivate ({selectedUsers.length})
                            </button>
                        )}
                    </div>

                    {/* Center: Search Bar — pr-12 reserves space so the collapsed filter icon never overlaps */}
                    <div className="flex-1 flex justify-center pr-12">
                        <div className="relative w-full max-w-md">
                            <FaSearch className="absolute left-3 top-1/3 -translate-y-1/2 text-campus-text-body" size={14} />
                            <input
                                id="user-search"
                                type="text"
                                placeholder="Search by name, email or ID…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 text-sm border border-campus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition bg-campus-surface"
                            />
                        </div>
                    </div>

                    {/* Right Side: Absolutely positioned so expansion never shifts the search bar */}
                    <div className="absolute right-0 top-1/8 -translate-y-1/2 z-20 flex gap-3 items-center">
                        <div
                            className="hover-expand-container bg-campus-surface border border-campus-border rounded-lg p-1 shadow-card"
                            style={{
                                '--expand-width': '25rem',
                                '--content-max-width': '23rem',
                                '--content-margin': '0.5rem'
                            }}
                        >
                            <div className="hover-expand-icon text-campus-text-body px-1 cursor-pointer">
                                <FiFilter size={18} />
                            </div>
                            <div className="hover-expand-content flex gap-1">
                                {ROLE_FILTERS.map((f) => (
                                    <button
                                        key={f.value}
                                        onClick={() => handleRoleChange(f.value)}
                                        className={`relative px-4 py-1.5 text-sm font-medium transition-all duration-300 rounded-md whitespace-nowrap ${roleFilter === f.value ? 'bg-campus-primary text-white shadow-sm' : 'bg-transparent text-campus-text-body hover:bg-campus-bg hover:text-campus-text-primary'}`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Animated Add User Button */}
                        {isAdmin && (
                            <button
                                onClick={() => setIsAddUserModalOpen(true)}
                                className="hover-expand-container bg-campus-primary text-white rounded-lg font-medium cursor-pointer h-10 px-3 hover:bg-campus-primary-dark shadow-sm hover:shadow-lg focus-visible:outline-none"
                                style={{
                                    '--expand-width': '8.5rem',
                                    '--expand-padding': '0 1rem'
                                }}
                                aria-label="Add User"
                            >
                                <span className="hover-expand-icon">
                                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                                </span>
                                <span className="hover-expand-content text-sm" aria-hidden="true">
                                    Add User
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <p className="text-campus-text-body">Loading users…</p>
                    </div>
                ) : (
                    <div className="bg-campus-surface rounded-xl shadow-card border border-campus-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-campus-border">
                                <thead className="bg-campus-bg relative">
                                    <tr>
                                        {isAdmin && (
                                            <th className="px-6 py-4 text-left">
                                                <input
                                                    type="checkbox"
                                                    checked={users.length > 0 && selectedUsers.length === users.length}
                                                    onChange={handleSelectAll}
                                                    className="w-4 h-4 text-campus-primary border-campus-border rounded focus:ring-campus-primary"
                                                />
                                            </th>
                                        )}
                                        {['Employee ID', 'Name', 'Role', 'Status'].map((col) => (
                                            <th
                                                key={col}
                                                className="px-6 py-4 text-left text-xs font-semibold text-campus-text-body uppercase tracking-wider"
                                            >
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-campus-surface divide-y divide-campus-border">
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 5 : 4} className="px-6 py-12 text-center text-campus-text-body text-sm">
                                                No users match your search or filter.
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map((user) => (
                                            <tr
                                                key={user.id}
                                                className={`hover:bg-campus-bg transition-colors duration-150 cursor-pointer ${isAdmin && selectedUsers.includes(user.id) ? 'bg-red-50' : ''}`}
                                                onClick={() => navigate(`/user/${user.id}`)}
                                            >
                                                {isAdmin && (
                                                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedUsers.includes(user.id)}
                                                            onChange={() => handleSelectUser(user.id)}
                                                            className="w-4 h-4 text-campus-primary border-campus-border rounded focus:ring-campus-primary"
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-campus-text-body">
                                                    {user.id_number || (
                                                        <span className="text-campus-text-body italic">—</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-campus-text-primary">{user.name}</div>
                                                    <div className="text-xs text-campus-text-body">{user.email}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${ROLE_BADGE[user.role] || 'bg-campus-bg text-campus-text-body'
                                                            }`}
                                                    >
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                    {isAdmin ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => confirmToggleStatus(user)}
                                                            className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-campus-primary focus:ring-offset-2 ${user.is_active ? 'bg-campus-success' : 'bg-gray-300'}`}
                                                            role="switch"
                                                            aria-checked={Boolean(user.is_active)}
                                                        >
                                                            <span className="sr-only">Toggle status</span>
                                                            <span
                                                                aria-hidden="true"
                                                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-300 ease-in-out ${user.is_active ? 'translate-x-5' : 'translate-x-0'}`}
                                                            />
                                                        </button>
                                                    ) : (
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {user.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        {!isLoading && users.length > 0 && (
                            <div className="px-6 py-4 border-t border-campus-border bg-campus-surface flex flex-col md:flex-row items-center justify-between gap-4 w-full">
                                <div className="flex flex-col sm:flex-row items-center gap-4 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-campus-text-body">Rows per page:</span>
                                        <select
                                            value={rowsPerPage}
                                            onChange={handleRowsPerPageChange}
                                            className="text-sm border border-campus-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-campus-primary bg-campus-surface text-campus-text-primary cursor-pointer hover:bg-campus-bg transition-colors"
                                        >
                                            {[10, 20, 25, 50].map((size) => (
                                                <option key={size} value={size}>
                                                    {size}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <span className="text-sm text-campus-text-body hidden sm:inline-block">|</span>
                                    <span className="text-sm text-campus-text-body">
                                        Showing <span className="font-semibold text-campus-text-primary">{((currentPage - 1) * rowsPerPage) + 1}</span> to <span className="font-semibold text-campus-text-primary">{Math.min(currentPage * rowsPerPage, totalUsers)}</span> of <span className="font-semibold text-campus-text-primary">{totalUsers}</span> users
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-campus-border text-campus-text-primary hover:bg-campus-bg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {getPageNumbers().map((pageNum, idx) => {
                                            let pageClass = 'text-campus-text-body hover:bg-campus-bg border border-transparent';
                                            if (pageNum === currentPage) pageClass = 'bg-campus-primary text-white border border-campus-primary shadow-sm';
                                            else if (pageNum === '...') pageClass = 'text-campus-text-body cursor-default';
                                            return (
                                                <button
                                                    key={`page-${pageNum}-${idx}`}
                                                    disabled={pageNum === '...'}
                                                    onClick={() => pageNum !== '...' && setCurrentPage(pageNum)}
                                                    className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-lg transition-colors ${pageClass}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <button
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-campus-border text-campus-text-primary hover:bg-campus-bg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {isAddUserModalOpen && (
                    <AddUserModal
                        closeAddUserModal={() => setIsAddUserModalOpen(false)}
                        fetchUsers={fetchUsers}
                    />
                )}
                
                <ConfirmationModal
                    isOpen={confirmModal.isOpen}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={closeConfirmModal}
                    confirmText={confirmModal.confirmText}
                    cancelText="Cancel"
                    confirmButtonClass={confirmModal.confirmButtonClass}
                />
            </main>
        </div>
    );
};

export default UserManagement;
